// connectivity.service.ts
import { Injectable, signal, computed } from '@angular/core';
import { fromEvent, merge, of } from 'rxjs';
import { map, shareReplay, startWith } from 'rxjs/operators';

const KEY = 'offlineModeEnabled';

@Injectable({ providedIn: 'root' })
export class ConnectivityService {
  // User toggle: "Pretend offline"
  readonly offlineModeEnabled = signal<boolean>(localStorage.getItem(KEY) === 'true');

  setOfflineMode(enabled: boolean) {
    this.offlineModeEnabled.set(enabled);
    localStorage.setItem(KEY, String(enabled));
  }
  toggleOfflineMode() {
    this.setOfflineMode(!this.offlineModeEnabled());
  }

  // Real device network status (browser)
  readonly browserOnline$ = merge(
    of(null),
    fromEvent(window, 'online'),
    fromEvent(window, 'offline')
  ).pipe(
    map(() => navigator.onLine),
    startWith(navigator.onLine),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  // “Effective online” (what your app should use)
  // If offline mode enabled => app is "offline" even if browser is online
  readonly isOnline$ = this.browserOnline$.pipe(
    map((browserOnline) => browserOnline && !this.offlineModeEnabled()),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  // Handy sync-friendly booleans (signals)
  readonly browserOnlineSig = signal<boolean>(navigator.onLine);
  readonly isOnlineSig = computed(() => this.browserOnlineSig() && !this.offlineModeEnabled());

  constructor() {
    // keep signals in sync for components that prefer signals
    this.browserOnline$.subscribe(v => this.browserOnlineSig.set(v));
  }
}